import React, { useEffect, useMemo, useState } from 'react';
import axios from 'helpers/api';
import { Alert, Card, Col, Row, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';

const METRIC_KEYS = [
  { label: 'Patients', keys: ['patient_requests'] },
  { label: 'Reports', keys: ['report_requests'] },
  { label: 'Prescriptions', keys: ['prescription_requests'] },
  { label: 'MedPass', keys: ['medpass_requests'] },
  { label: 'Orders', keys: ['order_requests'] }
];

const getNumericValue = (source, keys) => {
  if (!source) return 0;
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'number') return value;
    if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) {
      return Number(value);
    }
  }
  return 0;
};

const ActivityStats = ({ userId }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const fetchStats = async () => {
      setLoading(true);
      try {
        const tokenStr = localStorage.getItem('accessToken');
        const { data } = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/users/activity-stats/${userId}`,
          { headers: { Authorization: `Bearer ${tokenStr}` } }
        );
        setStats(data?.data ?? data ?? null);
      } catch (error) {
        setStats(null);
        toast.error('Failed to fetch user activity stats.');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [userId]);

  // const chartData = useMemo(() => {
  //   // const breakdown = stats?.breakdown || stats?.activity_breakdown || stats || {};
  //   const breakdown = stats || {};
  //   const items = METRIC_KEYS.map(metric => ({
  //     label: metric.label,
  //     value: getNumericValue(breakdown, metric.keys)
  //   }));
  //   const maxValue = Math.max(...items.map(item => item.value), 0);

  //   return {
  //     items,
  //     maxValue
  //   };
  // }, [stats]);

  const chartData = useMemo(() => {
    const breakdown = stats || {};

    const items = METRIC_KEYS.map(metric => ({
      label: metric.label,
      value: getNumericValue(breakdown, metric.keys)
    }));

    const maxValue = Math.max(...items.map(item => item.value), 0);

    return { items, maxValue };
  }, [stats]);

  const totalRequests = getNumericValue(stats, [
    'total_requests',
    'totalRequests',
    'requests',
    'request_count'
  ]);
  const activeDays = getNumericValue(stats, [
    'active_days',
    'activeDays',
    'days_active'
  ]);

  if (loading) {
    return (
      <Card className="mt-3">
        <Card.Body className="py-5 text-center">
          <Spinner animation="border" variant="primary" />
        </Card.Body>
      </Card>
    );
  }

  if (!userId) {
    return (
      <Card className="mt-3">
        <Card.Body>
          <Alert variant="warning" className="mb-0">
            User activity stats are unavailable because the user ID was not found.
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="mt-3">
      <div className='p-2'>
        <Row className="g-3 mb-4">
          <Col md={6}>
            <div className="border rounded-3 h-100 p-3 bg-body-tertiary">
              <div className="text-700 fs-10 text-uppercase fw-semibold mb-1">
                Total Requests
              </div>
              <div className="fs-4 fw-bold text-primary">{totalRequests}</div>
            </div>
          </Col>
          <Col md={6}>
            <div className="border rounded-3 h-100 p-3 bg-body-tertiary">
              <div className="text-700 fs-10 text-uppercase fw-semibold mb-1">
                Active Days
              </div>
              <div className="fs-4 fw-bold text-success">{activeDays}</div>
            </div>
          </Col>
        </Row>
        <div>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">Activity Breakdown</h6>
            {/* <span className="text-600 fs-11">Requests by module</span> */}
          </div>

          {chartData.items.every(item => item.value === 0) ? (
            <Alert variant="info" className="mb-0">
              No activity breakdown data available.
            </Alert>
          ) : (
            chartData.items.map(item => {
              const width =
                chartData.maxValue > 0 ? `${(item.value / chartData.maxValue) * 100}%` : '0%';

              return (
                <div key={item.label} className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="fw-semibold">{item.label}</span>
                    <span className="text-700 fs-11">{item.value}</span>
                  </div>
                  <div
                    className="rounded-pill overflow-hidden bg-body-tertiary"
                    style={{ height: '10px' }}
                  >
                    <div
                      className="rounded-pill"
                      style={{
                        width,
                        height: '100%',
                        minWidth: item.value > 0 ? '10px' : '0',
                        background:
                          'linear-gradient(90deg, rgba(44,123,229,0.95) 0%, rgba(0,196,140,0.85) 100%)'
                      }}
                    />
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </Card>
  );
};

export default ActivityStats;

